// pages/Investors.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Download, 
  TrendingUp, 
  Target, 
  Globe, 
  Award,
  ChevronRight,
  FileText,
  Calendar,
  Mail,
  Phone,
  BarChart3,
  Users,
  Briefcase,
  CheckCircle,
  ArrowUpRight,
  Lightbulb,
  Shield,
  Zap,
  Building2,
  Rocket,
  DollarSign
} from 'lucide-react';

const InvestorPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const observerRefs = useRef([]);

  useEffect(() => {
    // Smooth scroll behavior
    document.documentElement.classList.add('scroll-smooth');

    // Scroll position tracking for parallax
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            entry.target.classList.remove('animate-out');
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    // Observe all elements with data-animate attribute
    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    observerRefs.current = elements;

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
      document.documentElement.classList.remove('scroll-smooth');
    };
  }, []);

  const handleDownload = (fileName) => {
  const link = document.createElement('a');
  link.href = '/Vego-Rental-Revolutionizing-Urban-Mobility.pdf';
  link.download = 'Vego-Rental-Revolutionizing-Urban-Mobility.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Hero Section with Enhanced Parallax */}
      <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient Orbs with Parallax - Mobile Optimized */}
          <div 
            className="absolute -top-20 -right-20 sm:-top-40 sm:-right-40 w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] bg-blue-500 rounded-full opacity-20 blur-3xl"
            style={{ transform: `translate(${scrollY * 0.15}px, ${scrollY * 0.1}px)` }}
          />
          <div 
            className="absolute -bottom-20 -left-20 sm:-bottom-40 sm:-left-40 w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] bg-purple-500 rounded-full opacity-20 blur-3xl"
            style={{ transform: `translate(${-scrollY * 0.15}px, ${-scrollY * 0.1}px)` }}
          />
          <div 
            className="absolute top-1/2 left-1/2 w-[200px] h-[200px] sm:w-[400px] sm:h-[400px] bg-cyan-500 rounded-full opacity-10 blur-3xl"
            style={{ transform: `translate(-50%, -50%) scale(${1 + scrollY * 0.0005})` }}
          />
          
          {/* Floating Grid */}
          <div 
            className="absolute inset-0 bg-grid-pattern opacity-5"
            style={{ transform: `translateY(${scrollY * 0.5}px)` }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 z-10 w-full">
          <div className="max-w-5xl mx-auto text-center">
            {/* Status Badge with Pulse Animation - Mobile Responsive */}
            <div 
              className="inline-flex items-center bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-md border border-green-500/30 px-3 py-2 sm:px-6 sm:py-3 rounded-full mb-6 sm:mb-8"
              data-animate
            >
              <span className="relative flex h-2 w-2 sm:h-3 sm:w-3 mr-2 sm:mr-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 sm:h-3 sm:w-3 bg-green-500"></span>
              </span>
              <span className="text-xs sm:text-sm font-semibold tracking-wide">NOW RAISING SERIES A FUNDING</span>
            </div>

            {/* Main Heading with Gradient - Mobile Responsive */}
            <h1 
              className="text-3xl sm:text-5xl md:text-6xl lg:text-8xl font-bold mb-6 sm:mb-8 leading-tight px-2"
              data-animate
            >
              Invest in the Future of
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 animate-gradient">
                Urban Mobility
              </span>
            </h1>
            
            <p 
              className="text-base sm:text-xl lg:text-2xl mb-8 sm:mb-12 text-blue-100 leading-relaxed max-w-4xl mx-auto px-4"
              data-animate
            >
              Join us in revolutionizing two-wheeler rentals across India. Building sustainable, 
              technology-driven solutions for the next generation of urban transportation.
            </p>

            {/* CTA Buttons - Mobile Responsive */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center px-4" data-animate>
              <button
  onClick={() => handleDownload()}
  className="group relative bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 sm:px-10 py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg flex items-center justify-center gap-2 sm:gap-3 hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-2xl hover:shadow-cyan-500/50 transform hover:-translate-y-1 hover:scale-105"
>
  <Download className="w-5 h-5 sm:w-6 sm:h-6 group-hover:animate-bounce" />
  <span className="hidden sm:inline">Download Investor Deck</span>
  <span className="sm:hidden">Download Deck</span>
  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-2 transition-transform" />
  <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-r from-blue-400 to-cyan-400 opacity-0 group-hover:opacity-20 blur-xl transition-opacity" />
</button>


              <a
                href="/contact"
                className="group bg-white/10 backdrop-blur-md border-2 border-white/20 text-white px-6 sm:px-10 py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg flex items-center justify-center gap-2 sm:gap-3 hover:bg-white/20 hover:border-white/40 transition-all duration-300"
              >
                <Mail className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="hidden sm:inline">Schedule a Meeting</span>
                <span className="sm:hidden">Contact Us</span>
              </a>
            </div>
          </div>
        </div>

      </div>

      {/* Stats Section with Scroll Animation - Mobile Responsive */}
      <div className="bg-gradient-to-b from-slate-50 to-white py-12 sm:py-16 lg:py-32">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-10 sm:mb-16" data-animate>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
        Ve go by the Numbers
      </h2>
      <p className="text-base sm:text-xl text-gray-600 px-4">Real metrics, real growth, real opportunity</p>
    </div>

    <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
      {[
        { icon: TrendingUp, label: 'YoY Growth', value: 'Strong', subtext: 'Consistent Revenue Growth', color: 'blue', gradient: 'from-blue-500 to-cyan-500' },
        { icon: Users, label: 'Active Users', value: 'Growing', subtext: 'Expanding Customer Base', color: 'green', gradient: 'from-green-500 to-emerald-500' },
        { icon: Globe, label: 'Cities', value: 'Expanding', subtext: 'Multi-City Presence', color: 'purple', gradient: 'from-purple-500 to-pink-500' },
        { icon: Award, label: 'Customer Rating', value: '4.8/5', subtext: 'High Satisfaction', color: 'orange', gradient: 'from-orange-500 to-red-500' }
      ].map((stat, index) => (
        <div
          key={index}
          data-animate
          style={{ animationDelay: `${index * 100}ms` }}
          className="group relative bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 hover:border-transparent overflow-hidden"
        >
          {/* Background Gradient on Hover */}
          <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
          
          {/* Center all content on mobile */}
          <div className="relative z-10 flex flex-col items-center text-center">
            <div className={`bg-gradient-to-br ${stat.gradient} w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
              <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{stat.value}</h3>
            <p className="text-gray-600 font-semibold mb-1 text-sm sm:text-base">{stat.label}</p>
            <p className="text-xs sm:text-sm text-gray-500">{stat.subtext}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
</div>


      <div className="py-12 sm:py-16 lg:py-32 bg-white relative overflow-hidden">
  <div className="absolute inset-0 bg-gradient-radial from-blue-50 to-transparent opacity-50" />
  
  <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-12 sm:mb-20" data-animate>
      <span className="inline-block text-blue-600 font-semibold text-xs sm:text-sm tracking-wider uppercase mb-3 sm:mb-4">Our Journey</span>
      <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
        Vision & Growth Roadmap
      </h2>
      <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
        Vego Rental is on a mission to democratize mobility across India by making quality 
        two-wheelers accessible to everyone. We envision a future where owning a vehicle is 
        optional, and accessing one is effortless.
      </p>
    </div>

    {/* Roadmap Timeline - Mobile Responsive Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      {[
        {
        //   period: '2024-2025',
          title: 'Foundation',
          description: 'Consolidate presence in Maharashtra, launch in 3 new states',
          gradient: 'from-blue-500 to-cyan-500',
          icon: Building2
        },
        {
        //   period: '2025-2026',
          title: 'Expansion',
          description: 'Expand to 25+ cities, introduce EV fleet',
          gradient: 'from-purple-500 to-pink-500',
          icon: Rocket
        },
        {
        //   period: '2026-2027',
          title: 'Scale',
          description: 'Pan-India presence, achieve profitability',
          gradient: 'from-green-500 to-emerald-500',
          icon: TrendingUp
        },
        {
        //   period: '2027+',
          title: 'Leadership',
          description: 'Market leadership, explore international expansion',
          gradient: 'from-orange-500 to-red-500',
          icon: Globe
        }
      ].map((phase, index) => (
        <div
          key={index}
          data-animate
          style={{ animationDelay: `${index * 150}ms` }}
          className="group relative bg-gradient-to-br from-white to-gray-50 p-6 sm:p-8 rounded-2xl sm:rounded-3xl border-2 border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden"
        >
          {/* Background Gradient */}
          <div className={`absolute inset-0 bg-gradient-to-br ${phase.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
          
          {/* Center all content on mobile, left-align on larger screens */}
          <div className="relative z-10 flex flex-col items-center sm:items-start text-center sm:text-left">
            <div className={`bg-gradient-to-br ${phase.gradient} w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-xl`}>
              <phase.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-3 sm:mb-4 w-full">
              <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${phase.gradient}`} />
              <span className={`text-xs sm:text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r ${phase.gradient}`}>
                {phase.period}
              </span>
            </div>
            
            <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">{phase.title}</h4>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{phase.description}</p>
          </div>

          {/* Phase Number */}
          <div className={`absolute -top-3 -right-3 sm:-top-4 sm:-right-4 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${phase.gradient} opacity-10 rounded-full flex items-center justify-center`}>
            <span className="text-3xl sm:text-4xl font-bold text-gray-400">{index + 1}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
</div>


      {/* Custom Styles */}
      <style jsx>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }

        .bg-dot-pattern {
          background-image: radial-gradient(circle, #e5e7eb 1px, transparent 1px);
          background-size: 24px 24px;
        }

        .bg-grid-pattern {
          background-image: 
            linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 40px 40px;
        }

        [data-animate] {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        [data-animate].animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* Custom breakpoint for extra small devices */
        @media (min-width: 475px) {
          .xs\:grid-cols-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
          
          [data-animate] {
            opacity: 1;
            transform: none;
          }
        }
      `}</style>
    </div>
  );
};

export default InvestorPage;
